import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { message } from 'antd'
import requests from '../../../axios/requests'
import { setUserTrajectory } from '../../../../redux/actions/userActions'
import { sameName } from './genUtils'

// Учебный план организации (planning_skills): добавить/убрать навык и перечитать список с сервера.
// Сервер хранит только id навыков, поэтому счётчики планов и курсов считаем по списку планов пользователя.
const nameOf = (skill) => (typeof skill === 'string' ? skill : skill?.name)

// id навыка из справочника: у навыков из вакансий его может не быть
const resolveSkill = async (skill) => {
  if (skill && typeof skill.id === 'number') return { id: skill.id, name: skill.name }
  const name = nameOf(skill)
  if (!name) return null
  try {
    const { data } = await requests.profs.get_speciality_tags(name, 1, 10)
    const found = (data?.results || []).find((x) => sameName(x.name, name))
    return found ? { id: found.id, name: found.name } : null
  } catch (err) {
    console.error(err)
    return null
  }
}

const withCounts = (skills, plans, tests = []) =>
  (skills || []).map((sk) => {
    const own = plans.filter((pl) => pl.speciality_tag === sk.id)
    const courses = own.filter((pl) => pl.quest?.id).length
    return {
      ...sk,
      tests_count: tests.filter((t) => t.speciality_tag === sk.id).length,
      education_plans_count: own.length,
      generated_quests_count: courses || (sk.has_generated_quest ? 1 : 0),
      favorite: own.some((pl) => pl.favorite),
    }
  })

export default function useTrajectory() {
  const dispatch = useDispatch()
  const { trajectorySkills } = useSelector((state) => state.Reducer)
  const list = trajectorySkills || []

  const refresh = useCallback(
    () =>
      Promise.all([
        requests.auth.get_organization_info().then((r) => r.data?.planning_skills || []),
        requests.users
          .get_plannings()
          .then((r) => r.data?.results || [])
          .catch(() => []),
        requests.users
          .quiz_drafts({ created: 1 })
          .then((r) => (Array.isArray(r.data) ? r.data : []))
          .catch(() => []),
      ])
        .then(([skills, plans, tests]) => dispatch(setUserTrajectory(withCounts(skills, plans, tests))))
        .catch((err) => console.error(err)),
    [dispatch]
  )

  const save = useCallback(
    (ids) =>
      requests.auth
        .patch_organization_info({ planning_skills: ids })
        .then(refresh)
        .catch((err) => {
          console.error(err)
          message.error('Не удалось сохранить учебный план')
          refresh()
        }),
    [refresh]
  )

  const has = useCallback((name) => list.some((s) => sameName(s.name, name)), [list])

  const addMany = useCallback(
    async (skills) => {
      const names = []
      skills.forEach((sk) => {
        const name = nameOf(sk)
        if (name && !list.some((s) => sameName(s.name, name)) && !names.some((n) => sameName(nameOf(n), name)))
          names.push(sk)
      })
      if (!names.length) return
      dispatch(setUserTrajectory([...list, ...names.map((sk) => ({ id: `tmp-${nameOf(sk)}`, name: nameOf(sk) }))]))
      const resolved = await Promise.all(names.map(resolveSkill))
      const missing = names.filter((_, i) => !resolved[i]).map(nameOf)
      if (missing.length) message.warning(`Нет в справочнике навыков: ${missing.join(', ')}`)
      const ids = list.map((s) => s.id).filter((id) => typeof id === 'number')
      resolved.forEach((r) => r && !ids.includes(r.id) && ids.push(r.id))
      return save(ids)
    },
    [dispatch, list, save]
  )

  const add = useCallback((skill) => addMany([skill]), [addMany])

  const remove = useCallback(
    (skill) => {
      const name = nameOf(skill)
      if (!name) return Promise.resolve()
      const rest = list.filter((s) => !sameName(s.name, name))
      dispatch(setUserTrajectory(rest))
      return save(rest.map((s) => s.id).filter((id) => typeof id === 'number'))
    },
    [dispatch, list, save]
  )

  const toggle = useCallback((skill) => (has(nameOf(skill)) ? remove(skill) : add(skill)), [has, add, remove])

  return { list, has, add, addMany, remove, toggle, refresh }
}
